/**
 * The participant's own player profile (Jonathan 2026-05-21).
 *
 * Holds a display `name` + an `avatarSeed` (fed to
 * `AuthorIdentityService.forSeed` for the avatar pixels). Seeded with a random
 * name + avatar on first run, editable from the start menu and the pause menu,
 * persisted in `localStorage`. Static API mirrors `LanguageService`.
 *
 * No PII (PRD §11): the name is the player's free choice and the study issues
 * an anonymous participant id separately — the profile name never enters that
 * id and is not required to be real.
 */

import type { PlayerProfile } from "../types";

import { AUTHOR_NAME_POOL } from "./authorNamePool";

const STORAGE_KEY = "roamling.profile";

function readStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private-mode / no-storage: profile lives in the in-memory cache only.
    return;
  }
}

function isValid(p: unknown): p is Pick<PlayerProfile, "name" | "avatarSeed"> {
  return (
    typeof p === "object" &&
    p !== null &&
    typeof (p as PlayerProfile).name === "string" &&
    (p as PlayerProfile).name.length > 0 &&
    Number.isFinite((p as PlayerProfile).avatarSeed)
  );
}

/** Legacy profiles (pre-avatarKind) load as generated avatars. */
function normaliseKind(p: unknown): PlayerProfile["avatarKind"] {
  return (p as Partial<PlayerProfile>).avatarKind === "asset" ? "asset" : "generated";
}

export class ProfileService {
  private static cached: PlayerProfile | null = null;

  /** Current profile, loading (and seeding+persisting a random one) on first use. */
  static get(): PlayerProfile {
    if (this.cached) return this.cached;
    const raw = readStorage();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (isValid(parsed)) {
          this.cached = {
            name: parsed.name,
            avatarSeed: parsed.avatarSeed >>> 0,
            avatarKind: normaliseKind(parsed),
          };
          return this.cached;
        }
      } catch {
        // Corrupt entry — fall through to a fresh random profile.
      }
    }
    const fresh = this.randomProfile();
    this.cached = fresh;
    writeStorage(JSON.stringify(fresh));
    return fresh;
  }

  /** Persist an edited profile and update the in-memory cache. */
  static save(profile: PlayerProfile): void {
    const clean: PlayerProfile = {
      name: profile.name.trim().slice(0, 16) || this.randomName(),
      avatarSeed: profile.avatarSeed >>> 0,
      avatarKind: profile.avatarKind === "asset" ? "asset" : "generated",
    };
    this.cached = clean;
    writeStorage(JSON.stringify(clean));
  }

  /** Replace the whole profile with a fresh random one and persist it. */
  static randomize(): PlayerProfile {
    const fresh = this.randomProfile();
    this.save(fresh);
    return fresh;
  }

  /** A fresh random avatar seed (for the "new avatar" affordance). */
  static randomAvatarSeed(): number {
    return (Math.floor(Math.random() * 0xffffffff) >>> 0) || 1;
  }

  private static randomName(): string {
    return AUTHOR_NAME_POOL[Math.floor(Math.random() * AUTHOR_NAME_POOL.length)]!;
  }

  private static randomProfile(): PlayerProfile {
    return { name: this.randomName(), avatarSeed: this.randomAvatarSeed(), avatarKind: "generated" };
  }
}
